import { useRef } from "react";
import { curveToLUT } from "../../lib/adjustments";

const SIZE = 220;

// Interactive curves graph: click to add points, drag to shape, double-click to remove.
export default function CurveEditor({ points, onChange, onStart }) {
  const svgRef = useRef(null);
  const dragIdx = useRef(null);
  const pts = (points && points.length ? points : [[0, 0], [255, 255]]);

  const toSvg = ([x, y]) => [(x / 255) * SIZE, SIZE - (y / 255) * SIZE];
  const toData = (px, py) => [
    Math.round(Math.min(255, Math.max(0, (px / SIZE) * 255))),
    Math.round(Math.min(255, Math.max(0, (1 - py / SIZE) * 255))),
  ];

  const localXY = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };

  const handleDown = (e) => {
    onStart && onStart();
    const [px, py] = localXY(e);
    // hit-test existing point
    let hit = -1;
    pts.forEach((p, i) => {
      const [sx, sy] = toSvg(p);
      if (Math.hypot(sx - px, sy - py) < 12) hit = i;
    });
    if (hit === -1) {
      const [dx, dy] = toData(px, py);
      const next = [...pts, [dx, dy]].sort((a, b) => a[0] - b[0]);
      hit = next.findIndex((p) => p[0] === dx && p[1] === dy);
      onChange(next);
    }
    dragIdx.current = hit;
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
  };

  const handleMove = (e) => {
    if (dragIdx.current == null) return;
    const [px, py] = localXY(e);
    let [dx, dy] = toData(px, py);
    const i = dragIdx.current;
    const isFirst = i === 0, isLast = i === pts.length - 1;
    // endpoints keep their x; interior points stay between neighbours
    if (isFirst) dx = 0;
    else if (isLast) dx = 255;
    else dx = Math.min(pts[i + 1][0] - 1, Math.max(pts[i - 1][0] + 1, dx));
    const next = pts.map((p, idx) => (idx === i ? [dx, dy] : p));
    onChange(next);
  };

  const handleUp = () => {
    dragIdx.current = null;
    window.removeEventListener("pointermove", handleMove);
    window.removeEventListener("pointerup", handleUp);
  };

  const removePoint = (i) => {
    if (i === 0 || i === pts.length - 1) return;
    onStart && onStart();
    onChange(pts.filter((_, idx) => idx !== i));
  };

  const lut = curveToLUT(pts);
  let path = `M 0 ${SIZE - (lut[0] / 255) * SIZE}`;
  for (let i = 1; i < 256; i++) path += ` L ${(i / 255) * SIZE} ${SIZE - (lut[i] / 255) * SIZE}`;

  return (
    <svg
      ref={svgRef}
      width="100%"
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      onPointerDown={handleDown}
      data-testid="curve-editor"
      style={{ touchAction: "none", background: "#0b0b0b", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", cursor: "crosshair" }}
    >
      {[0.25, 0.5, 0.75].map((g) => (
        <g key={g} stroke="rgba(255,255,255,0.07)">
          <line x1={g * SIZE} y1="0" x2={g * SIZE} y2={SIZE} />
          <line x1="0" y1={g * SIZE} x2={SIZE} y2={g * SIZE} />
        </g>
      ))}
      <line x1="0" y1={SIZE} x2={SIZE} y2="0" stroke="rgba(255,255,255,0.12)" strokeDasharray="3 3" />
      <path d={path} fill="none" stroke="#3b82f6" strokeWidth="2" />
      {pts.map((p, i) => {
        const [sx, sy] = toSvg(p);
        return (
          <circle
            key={i}
            cx={sx} cy={sy} r="5.5"
            fill="#fff" stroke="#3b82f6" strokeWidth="2"
            data-testid={`curve-point-${i}`}
            onDoubleClick={(e) => { e.stopPropagation(); removePoint(i); }}
            style={{ cursor: "grab" }}
          />
        );
      })}
    </svg>
  );
}
